import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, filter, finalize, map, merge, Observable, of, Subject, switchMap, take, tap, throwError } from 'rxjs';
import { Site } from './sites.types';
import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { SvcCacheName } from '../../../services/cache/svc-cache-name.enum';
import { SvcDataCacheService } from '../../../services/cache/svc-data-cache.service';
import { UserService } from '../../user/services/user.service';
import { AuthService } from '../../../auth/auth.service';
import { SiteBase } from './site.base';

@Injectable({
  providedIn: 'root',
})
export class SitesService {
  private _sites = new BehaviorSubject<Site[]>([]);
  public sites$ = this._sites.asObservable();
  public currentSite: Site;

  private _sitesPendingRequest: Subject<Site[]>;
  private set _sitesCached(value: Site[]) {
    this._svcDataCacheService.set<Site[]>(SvcCacheName.SITES, this._normalizeSites(value));
  }
  private get _sitesCached() {
    const sites = this._normalizeSites(this._svcDataCacheService.get<Site[]>(SvcCacheName.SITES) ?? []);
    return sites.length > 0 ? sites : null;
  }

  constructor(
    private _httpClient: HttpClient,
    private _appConfig: AppEnvironmentConfig,
    private _userService: UserService,
    private _svcDataCacheService: SvcDataCacheService,
    private _authService: AuthService
  ) {
    this._userService.user$
      .pipe(
        filter((user) => !!user),
        tap(() => this._checkCurrentSite())
      )
      .subscribe();
    merge(this._authService.signIn$, this._authService.signOut$).subscribe(() => {
      this._sites = new BehaviorSubject<Site[]>([]);
      this.sites$ = this._sites.asObservable();
      this.currentSite = null;
    });
  }

  private _checkCurrentSite() {
    const user = this._userService.user;
    if (!user) {
      this.currentSite = null;
      return;
    }

    if (!user?.lastSiteId) {
      return;
    }

    if (!user?.lastSiteId) {
      return;
    }

    const sites = this._normalizeSites(this._sites.value);
    if (sites?.length > 0) {
      sites.forEach((site) => {
        site.isSiteCurrent = site.siteId === user.lastSiteId;
      });
      this._sitesCached = sites;
      this.currentSite = sites.find((x) => x.isSiteCurrent);
      this._sites.next(sites);
    }
  }

  private _applyCurrentSite(sites: Site[]): void {
    sites = this._normalizeSites(sites);
    const userLastSiteId = this._userService.user?.lastSiteId;

    if (userLastSiteId) {
      sites.forEach((site) => {
        site.isSiteCurrent = site.siteId === userLastSiteId;
      });
      this.currentSite = sites.find((x) => x.isSiteCurrent) ?? null;
    } else {
      this.currentSite = this.currentSite ?? null;
    }

    this._sitesCached = sites;
    this._sites.next(sites);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all sites
   */
  public getAll(): Observable<Site[]> {
    return this._authService.check().pipe(
      switchMap((authenticated) => {
        if (!authenticated) return of([]);
        if (!this._sitesPendingRequest && !this._sitesCached) {
          this._sitesPendingRequest = new Subject<Site[]>();
          const pendingRequest = this._sitesPendingRequest;
          const cancelProp = (): void => (this._sitesPendingRequest = null);
          this._httpClient
            .get<Site[]>(`${this._appConfig.APIs.apiUrlAuth}/site`)
            .pipe(
              tap((sites) => {
                sites = this._normalizeSites(sites);
                cancelProp();
                pendingRequest.next(sites);
              }),
              catchError((error) => {
                cancelProp();
                pendingRequest.error(error);
                return throwError(() => error);
              }),
              finalize(() => {
                cancelProp();
                pendingRequest.complete();
              })
            )
            .subscribe();
        }
        return (this._sitesCached ? of(this._sitesCached) : this._sitesPendingRequest).pipe(
          tap((sites) => {
            this._applyCurrentSite(sites);
            this._checkCurrentSite();
          })
        );
      })
    );
  }

  /**
   * Get all activated sites
   */
  public getAllActiveByEnvAuthanticated(options?: { ignoreCurrent?: boolean }): Observable<SiteBase[]> {
    const withCurrent = !(options?.ignoreCurrent ?? false);
    return this._httpClient.get<SiteBase[]>(`${this._appConfig.APIs.apiUrlAuth}/Site/ByEnvironment/authenticated`).pipe(
      switchMap((sites) => {
        if (!withCurrent) {
          return this.sites$.pipe(
            take(1),
            map(() => (this.currentSite ? sites.filter((s) => s.siteId !== this.currentSite.siteId) : sites))
          );
        }
        return of(sites);
      })
    );
  }

  /**
   * Update the active site
   *
   * @param id
   */
  public update(id: number): Observable<{ isSucess: boolean }> {
    return this._httpClient.put<{ isSucess: boolean }>(`${this._appConfig.APIs.apiUrlAuth}/UserPreference/change-site`, { siteId: id }).pipe(
      tap((done) => {
        if (done.isSucess) {
          this.currentSite = null;
          const sites = this._normalizeSites(this._sitesCached ?? this._sites.value).map((site) => {
            site.isSiteCurrent = site.siteId === id;
            if (site.isSiteCurrent) {
              this.currentSite = site;
            }
            return site;
          });
          this._sitesCached = sites;
          this._sites.next(sites);
        }
      })
    );
  }

  private _normalizeSites(sites: Site[] | null | undefined): Site[] {
    const result = new Map<number, Site>();
    (sites ?? []).forEach((site) => {
      if (site?.siteId != null && !result.has(site.siteId)) {
        result.set(site.siteId, site);
      }
    });
    return [...result.values()];
  }
}
